import { create } from 'zustand';
import axiosInstance from '@/plugins/axios';

interface PermissionsState {
  permissions: string[];
  isSuper: boolean;
  loaded: boolean;
  loading: boolean;
  fetchPermissions: () => Promise<boolean>;
  hasPermission: (code: string) => boolean;
  hasAnyPermission: (codes: string | string[]) => boolean;
  reset: () => void;
}

let inflight: Promise<boolean> | null = null;

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissions: [],
  isSuper: false,
  loaded: false,
  loading: false,

  fetchPermissions() {
    if (get().loaded) return Promise.resolve(true);
    if (inflight) return inflight;

    set({ loading: true });
    const request: Promise<boolean> = (async () => {
      try {
        // BFF /auth/my-permissions → { permissions: string[], is_super: boolean }.
        // O super-admin resolve com permissions vazias no backend (bypass é no
        // require_permission), então o front PRECISA do flag pra não bloqueá-lo.
        const { data } = await axiosInstance.get('auth/my-permissions');
        set({
          permissions: data?.permissions ?? [],
          isSuper: Boolean(data?.is_super),
          loaded: true,
        });
        return true;
      } catch (e) {
        console.error('Failed to fetch permissions:', e);
        return false;
      }
    })().finally(() => {
      if (inflight === request) {
        inflight = null;
        set({ loading: false });
      }
    });
    inflight = request;
    return request;
  },

  hasPermission(code) {
    return get().isSuper || get().permissions.includes(code);
  },

  hasAnyPermission(codes) {
    if (get().isSuper) return true;
    const list = Array.isArray(codes) ? codes : [codes];
    return list.some((code) => get().permissions.includes(code));
  },

  reset() {
    // Limpa loading também: uma request cancelada (controller.abort no logout) deixa
    // fetchPermissions preso com loading=true (a promise nunca resolve → finally não
    // roda). Sem zerar aqui, o guard `if (loaded || loading) return` bloquearia todo
    // fetch pós re-login e o usuário veria "sem acesso" até dar refresh.
    inflight = null;
    set({ permissions: [], isSuper: false, loaded: false, loading: false });
  },
}));
